import React from 'react';

import { Box, Flex, rem, useColorMode, useTheme } from '@vimeo/bokeh';

import { PLANS } from '../../../utils/constants';
import { defaultTemplateProps, PlanData, UserData } from '../../../utils/interface';
import translations from '../../../utils/translations';
import CTAButton from '../../common/CTAButton';
import PlanCardFeatures from './PlanCardFeatures';
import PlanCardHeader from './PlanCardHeader';
import PlanCardPrice from './PlanCardPrice';
import PlanCardQuota from './PlanCardQuota';
import { RecommendedBadge } from './styles';

interface Props {
  planData: PlanData;
  userData: UserData;
  previousCardPlan: string | null;
  modalConfig?: defaultTemplateProps;
  showRecommendedBadge: boolean;
  displayMonthlyPlan: boolean;
  hideSubheading: boolean;
  allowFreeTrial?: boolean;
  showStorageQuotaModalUpdates?: boolean;
}

const PlanCard = (props: Props) => {
  const isEnterprise = props.planData.tier === PLANS.ENTERPRISE;
  const theme = useTheme();
  const { showRecommendedBadge } = props;
  const { colorMode } = useColorMode();
  const {
    semanticTokens: { colors },
  } = theme;

  const themeColor = colorMode === 'dark' ? colors['text-primary'].default : 'white';
  const hoverColor = colorMode === 'dark' ? colors.grayscale['200'] : colors.grayscale['700'];

  return (
    <Box
      display="flex"
      maxW={{ base: '100%', xl: '19.5rem' }}
      padding={{ base: 'lg', md: 'xl', xl: 'lg' }}
      flexDir="column"
      alignItems="flex-start"
      gap={{ base: 'md', md: 'lg', xl: 'md' }}
      flex="1 0 0"
      alignSelf="stretch"
      background={themeColor}
      borderRadius={rem(20)}
      border={showRecommendedBadge ? '4px solid #8a5ee8' : `${rem(1)} solid ${colors.slate['200']}`}
      borderTop={showRecommendedBadge ? 'none' : undefined}
      marginTop={{ base: showRecommendedBadge ? rem(20) : undefined, xl: 0 }}
      position="relative"
      width={{ base: '100%', md: rem(618), xl: '100%' }}
      flexDirection={{ base: 'column', md: 'row', xl: 'column' }}
    >
      {props.showRecommendedBadge ? (
        <RecommendedBadge>{translations.recommended}</RecommendedBadge>
      ) : null}
      <Flex gap="1rem" flexDirection="column" width={{ base: '100%', md: rem(220), xl: '100%' }}>
        <PlanCardHeader
          planData={props.planData}
          modalConfig={props.modalConfig}
          hideSubheading={props.hideSubheading}
        />
        <PlanCardPrice planData={props.planData} displayMonthlyPlan={props.displayMonthlyPlan} />
        <CTAButton
          planData={props.planData}
          ctaTreatment={props.modalConfig?.ctaTreatment}
          allowFreeTrial={props?.allowFreeTrial}
          displayMonthlyPlan={props.displayMonthlyPlan}
          color={isEnterprise ? themeColor : '#0a0e12'}
          backgroundColor={
            isEnterprise
              ? theme?.modal?.planCard?.background ?? colors['text-primary'].default
              : colors['fill-brand'].default
          }
          border={isEnterprise ? `1px solid ${theme?.modal?.enterprise?.border ?? '#000'}` : 'none'}
          buttonBottomSpacing="0"
          whiteSpace="break-spaces"
        />
      </Flex>
      <Flex gap="lg" flexDirection="column">
        <PlanCardQuota
          modalConfig={props.modalConfig}
          planData={props.planData}
          displayMonthlyPlan={props.displayMonthlyPlan}
          showStorageQuotaModalUpdates={props.showStorageQuotaModalUpdates}
        />
        <PlanCardFeatures
          planData={props.planData}
          modalConfig={props.modalConfig}
          previousCardPlan={props.previousCardPlan}
        />
      </Flex>
    </Box>
  );
};

export default PlanCard;
